import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Layers, Shield, Cpu, Search,Rocket,RefreshCw } from 'lucide-react';

const services = [
 {
    id: 'system-design',
    icon: Layers,
    title: 'System Design & Architecture',
    subtitle: 'FROM AMBIGUITY TO SPEC',
    description: 'Turning business ambiguity into concrete technical specs.',
    tags: ['distributed-systems', 'api-design', 'microservices'],
    humanLabel: 'messy requirements',
    aiLabel: 'CLEAN ARCHITECTURE',
    span: 'md:col-span-1 md:row-span-1',
  },
  {
    id: 'cloud-devops',
    icon: Shield,
    title: 'Cloud Infrastructure & DevOps',
    subtitle: 'SECURE BY DESIGN',
    description: 'AWS/Cloudflare setups that are secure by design, not just default settings.',
    tags: ['aws', 'cloudflare', 'docker', 'ci/cd'],
    humanLabel: 'manual deploys',
    aiLabel: 'AUTOMATED PIPELINES',
    span: 'md:col-span-1 md:row-span-1',
  },
  {
    id: 'backend-eng',
    icon: Cpu,
    title: 'Backend Engineering',
    subtitle: 'HIGH PERFORMANCE',
    description: 'High-performance code (Go, Rust, Node.js) verified for memory safety and scale.',
    tags: ['go', 'rust', 'node.js', 'postgresql'],
    humanLabel: 'raw logic',
    aiLabel: 'OPTIMIZED CODE',
    span: 'md:col-span-1 md:row-span-1',
  },
  {
    id: 'audit',
    icon: Search,
    title: 'The "Human-in-the-Loop" Audit',
    subtitle: 'AI CODE REVIEW',
    description: 'Reviewing your AI-generated codebases for security flaws and technical debt.',
    tags: ['security-audit', 'code-review', 'tech-debt'],
    humanLabel: 'gut feeling',
    aiLabel: 'STATIC ANALYSIS',
    span: 'md:col-span-1 md:row-span-1',
  },
  {
    id: 'legacy-modernization',
    icon: RefreshCw, 
    title: 'Legacy Modernization',
    subtitle: 'TECH DEBT TO ASSET',
    description: 'AI-accelerated refactoring of aging monoliths and outdated databases into modern, maintainable standards.',
    tags: ['refactoring', 'monolith-to-microservices', 'database-migration'],
    humanLabel: 'tangled legacy code',
    aiLabel: 'MODERNIZED REPO',
    span: 'md:col-span-1 md:row-span-1',
  },
  {
    id: 'ai-native-mvp',
    icon: Rocket,
    title: 'AI-Native MVP Engineering',
    subtitle: 'SPEED MEETS SCALE',
    description: 'Transforming abstract business ideas into production-ready products rapidly, with foundational architecture built to scale from day one.',
    tags: ['rapid-prototyping', 'full-stack', 'mvp-build'],
    humanLabel: 'business vision',
    aiLabel: 'SHIPPED PRODUCT',
    span: 'md:col-span-1 md:row-span-1',
  }
];

const ServiceCard = ({ service, index, isInView }) => {
  const Icon = service.icon;

  return (
    <motion.div
      data-testid={`service-card-${service.id}`}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: 0.15 * index }}
      className={`${service.span} group relative border border-ai-secondary/15 hover:border-ai-primary/30 bg-ai-bg/40 backdrop-blur-sm p-6 md:p-8 transition-colors duration-500`}
    >
      {/* Corner decorations */}
      <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-ai-primary/20 group-hover:border-ai-primary/60 transition-colors duration-500" />
      <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-ai-primary/20 group-hover:border-ai-primary/60 transition-colors duration-500" />
      <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-human-primary/20 group-hover:border-human-primary/60 transition-colors duration-500" />
      <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-human-primary/20 group-hover:border-human-primary/60 transition-colors duration-500" />

      {/* Background glow on hover */}
      <div className="absolute inset-0 bg-gradient-to-br from-ai-primary/[0.02] to-human-primary/[0.02] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      <div className="relative z-10">
        {/* Icon + Subtitle */}
        <div className="flex items-start justify-between mb-6">
          <div className="p-2 border border-ai-secondary/20 group-hover:border-ai-primary/30 transition-colors duration-300">
            <Icon size={18} className="text-ai-primary/60 group-hover:text-ai-primary transition-colors duration-300" strokeWidth={1.5} />
          </div>
          <span className="text-ai-text/60 text-xs uppercase tracking-[0.3em] font-semibold">{service.subtitle}</span>
        </div>

        {/* Title */}
        <h3 className="text-ai-text text-base md:text-lg font-semibold uppercase tracking-wide mb-3 group-hover:text-ai-primary transition-colors duration-300">
          {service.title}
        </h3>

        {/* Description */}
        <p className="text-human-text/70 text-sm italic leading-relaxed mb-6 font-light">
          {service.description}
        </p>

        {/* Human -> AI flow indicator */}
        <div className="flex items-center gap-3 mb-4">
          <span className="text-xs italic text-human-primary font-light">{service.humanLabel}</span>
          <motion.div
            className="flex-1 h-px relative"
            style={{ background: 'linear-gradient(90deg, rgba(224,90,71,0.3), rgba(0,240,255,0.3))' }}
          >
            <motion.div
              className="absolute top-0 left-0 w-2 h-px bg-ai-primary"
              animate={{ left: ['0%', '100%', '0%'] }}
              transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
            />
          </motion.div>
          <span className="text-xs uppercase text-ai-primary font-semibold tracking-wider">{service.aiLabel}</span>
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-2">
          {service.tags.map((tag) => (
            <span key={tag} className="text-xs text-ai-text/80 border border-ai-secondary/30 px-2 py-0.5 uppercase tracking-wider">
              {tag}
            </span>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

export const ServicesSection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });

  return (
    <section
      id="services"
      ref={sectionRef}
      data-testid="services-section"
      className="relative py-24 md:py-36 bg-ai-bg"
    >
      {/* Grid background */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `
          linear-gradient(rgba(0,240,255,0.3) 1px, transparent 1px),
          linear-gradient(90deg, rgba(0,240,255,0.3) 1px, transparent 1px)
        `,
        backgroundSize: '60px 60px',
      }} />

      <div className="relative z-10 max-w-6xl mx-auto px-6">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="mb-16"
        >
          <span className="text-ai-text/50 text-xs uppercase tracking-[0.4em] block mb-4">{'// 02'}</span>
          <h2 className="text-2xl md:text-4xl leading-tight">
            <span className="font-bold uppercase text-ai-text tracking-wider">HYBRID</span>
            {' '}
            <span className="italic text-human-text font-light">Services</span>
          </h2>
        </motion.div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {services.map((service, i) => (
            <ServiceCard key={service.id} service={service} index={i} isInView={isInView} />
          ))}
        </div>
      </div>
    </section>
  );
};
